import { Badge } from '@artic/ui/badge';
import { useTranslation } from 'react-i18next';

import type { ProjectStatus } from './types';

/**
 * All three states are shared business vocabulary, so they come from
 * `@artic/i18n` `terms.*` rather than a project-only namespace — the same
 * labels appear on the stepper, the planner and the hub.
 */
const STATE_TERM_KEYS = {
  draft: 'terms.draft',
  released: 'terms.released',
  redrafting: 'terms.redrafting',
} as const satisfies Record<ProjectStatus, string>;

interface ProjectStateChipProps {
  projectStatus: ProjectStatus;
}

/**
 * ENG-720 Band 1: the project's lifecycle state, rendered inline with the
 * breadcrumb via the route's `breadcrumbBadges` resolver. A single slot —
 * `Released` (green, with a dot), `Redrafting` (dark) or `Draft` (outline).
 *
 * `project_status` is a stored tri-state (`draft | released | redrafting` —
 * ENG-830), not derived, so the chip reads it directly.
 *
 * Only `Released` gets a status colour: it's the one state that says "this
 * plan is live on the floor". Draft and Redrafting are working states and
 * borrow the shadcn Badge defaults rather than inventing tones for them.
 */
export function ProjectStateChip({ projectStatus }: ProjectStateChipProps) {
  const { t } = useTranslation('common');
  const state = projectStatus;

  if (state === 'released') {
    return (
      <Badge
        variant="outline"
        data-slot="project-state-chip"
        data-state="released"
        className="border-status-good/30 bg-status-good/10 text-status-good-text gap-1.5"
      >
        <span aria-hidden className="bg-status-good size-1.5 rounded-full" />
        {t(STATE_TERM_KEYS.released)}
      </Badge>
    );
  }

  return (
    <Badge
      variant={state === 'redrafting' ? 'default' : 'outline'}
      data-slot="project-state-chip"
      data-state={state}
    >
      {t(STATE_TERM_KEYS[state])}
    </Badge>
  );
}
